import { useCallback, useEffect, useRef, useState } from 'react'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

export type TooltipPlacement = {
  position: TooltipPosition
  horizontalOffset: number
  arrowOffset: number
  left: number
  top: number
  transform: string
  isOutOfViewport: boolean
}

type UseTooltipPositionProps = {
  map: H.Map
  latitude: number
  longitude: number
  tooltipWidth?: number
  tooltipHeight?: number
  verticalGap?: number
  edgePadding?: number
  verticalTop?: number
}

const DEFAULT_TOOLTIP_WIDTH = 390
const DEFAULT_TOOLTIP_HEIGHT = 200
const POSITION_DECISION_TOP_GAP = 28
const DEFAULT_VERTICAL_TOP_GAP = 60
const DEFAULT_VERTICAL_GAP = 10
const DEFAULT_EDGE_PADDING = 16
const MIN_ARROW_LEFT_PERCENT = 5
// PIN_W = 27px, anchor at center → right edge at +13.5px from screenX.
// Arrow tip = screenX + SIDE_HORIZONTAL_GAP - 7 (half the 30px rotated square).
// 19 places the tip at ~15px — just clear of the pin's right edge.
const SIDE_HORIZONTAL_GAP = 19
// Fixed height used for the above/below position decision — must not change
// as tooltip content loads. Using the measured tooltipHeight here caused
// skeleton→loaded flicker because the height changed and flipped the decision.
// Value mirrors master's POSITION_DECISION_HEIGHT (SH-875 rework).
const POSITION_DECISION_HEIGHT = 390

export function computeTooltipPlacement(
  screenX: number,
  screenY: number,
  viewportWidth: number,
  viewportHeight: number,
  {
    tooltipWidth,
    tooltipHeight,
    verticalGap,
    edgePadding,
    verticalTop,
  }: {
    tooltipWidth: number
    tooltipHeight: number
    verticalGap: number
    edgePadding: number
    verticalTop: number
  },
): TooltipPlacement {
  const isOutOfViewport =
    screenX < 0 ||
    screenX > viewportWidth ||
    screenY < 0 ||
    screenY > viewportHeight

  if (isOutOfViewport) {
    return { ...getDefaultPlacement(), isOutOfViewport: true }
  }

  const halfTooltipWidth = tooltipWidth / 2

  // Pins too close to the left or right edge can't keep the arrow connected
  // when the bubble is placed above/below — switch to a side position instead
  // (bubble appears to the right of a left-edge pin, left of a right-edge pin).
  // Mirrors master's side-positioning branch (SH-895).
  const sideThreshold =
    edgePadding + (MIN_ARROW_LEFT_PERCENT / 100) * tooltipWidth
  const nearLeftEdge = screenX < sideThreshold
  const nearRightEdge = screenX > viewportWidth - sideThreshold

  if (nearLeftEdge || nearRightEdge) {
    return computeSidePlacement(
      nearLeftEdge ? 'right' : 'left',
      screenX,
      screenY,
      viewportHeight,
      tooltipHeight,
      edgePadding,
    )
  }

  // Use the pin's visual top as the space reference so the above/below
  // decision accounts for the full pin body height (SH-875 rework).
  const pinTopY = screenY - POSITION_DECISION_TOP_GAP
  const spaceAbove = pinTopY
  const spaceBelow = viewportHeight - screenY
  const needsBottomPosition =
    spaceAbove < POSITION_DECISION_HEIGHT + edgePadding
  const preferredPosition: TooltipPosition =
    needsBottomPosition && spaceBelow > spaceAbove ? 'bottom' : 'top'

  const fitsAbove = screenY - verticalTop - tooltipHeight >= edgePadding
  const fitsBelow =
    screenY + verticalGap + tooltipHeight <= viewportHeight - edgePadding
  const fits = (candidate: TooltipPosition) =>
    candidate === 'top' ? fitsAbove : fitsBelow
  const oppositePosition: TooltipPosition =
    preferredPosition === 'top' ? 'bottom' : 'top'

  let position = preferredPosition
  if (!fits(preferredPosition)) {
    if (fits(oppositePosition)) {
      position = oppositePosition
    } else {
      const sideWidth = SIDE_HORIZONTAL_GAP + tooltipWidth
      const spaceRight = viewportWidth - edgePadding - screenX
      const spaceLeft = screenX - edgePadding
      if (spaceRight >= sideWidth || spaceLeft >= sideWidth) {
        return computeSidePlacement(
          spaceRight >= sideWidth ? 'right' : 'left',
          screenX,
          screenY,
          viewportHeight,
          tooltipHeight,
          edgePadding,
        )
      }
    }
  }

  let horizontalOffset = 0

  const leftEdge = screenX - halfTooltipWidth
  if (leftEdge < edgePadding) {
    horizontalOffset = edgePadding - leftEdge
  }

  const rightEdge = screenX + halfTooltipWidth
  if (rightEdge > viewportWidth - edgePadding) {
    horizontalOffset = viewportWidth - edgePadding - rightEdge
  }

  const left = screenX + horizontalOffset
  const top = position === 'top' ? screenY - verticalTop : screenY + verticalGap
  const transform =
    position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0%)'

  // For horizontal bubbles, arrow offset points to screenX
  // arrowOffset is the pixel distance from bubble center to pin center
  const arrowOffset = screenX - left

  return {
    position,
    horizontalOffset,
    arrowOffset,
    left,
    top,
    transform,
    isOutOfViewport: false,
  }
}

function computeSidePlacement(
  position: 'left' | 'right',
  screenX: number,
  screenY: number,
  viewportHeight: number,
  tooltipHeight: number,
  edgePadding: number,
): TooltipPlacement {
  const halfTooltipHeight = tooltipHeight / 2
  const topEdge = screenY - halfTooltipHeight
  const bottomEdge = screenY + halfTooltipHeight

  let verticalOffset = 0
  if (topEdge < edgePadding) {
    verticalOffset = edgePadding - topEdge
  } else if (bottomEdge > viewportHeight - edgePadding) {
    verticalOffset = viewportHeight - edgePadding - bottomEdge
  }

  const left =
    position === 'right'
      ? screenX + SIDE_HORIZONTAL_GAP
      : screenX - SIDE_HORIZONTAL_GAP
  const top = screenY + verticalOffset

  // For vertical bubbles, arrow offset points to screenY
  // arrowOffset is the pixel distance from bubble center to pin center
  const arrowOffset = screenY - top
  const transform =
    position === 'right' ? 'translate(0, -50%)' : 'translate(-100%, -50%)'

  return {
    position,
    horizontalOffset: 0,
    arrowOffset,
    left,
    top,
    transform,
    isOutOfViewport: false,
  }
}

export function useTooltipPosition({
  map,
  latitude,
  longitude,
  tooltipWidth = DEFAULT_TOOLTIP_WIDTH,
  tooltipHeight = DEFAULT_TOOLTIP_HEIGHT,
  verticalGap = DEFAULT_VERTICAL_GAP,
  edgePadding = DEFAULT_EDGE_PADDING,
  verticalTop = DEFAULT_VERTICAL_TOP_GAP,
}: UseTooltipPositionProps): TooltipPlacement {
  const [placement, setPlacement] = useState<TooltipPlacement>(() =>
    getDefaultPlacement(),
  )

  const calculatePlacement = useCallback(() => {
    if (!map) return getDefaultPlacement()

    const screenPos = map.geoToScreen({ lat: latitude, lng: longitude })
    if (!screenPos) return getDefaultPlacement()

    const mapElement = map.getElement()
    if (!mapElement) return getDefaultPlacement()

    return computeTooltipPlacement(
      screenPos.x,
      screenPos.y,
      mapElement.clientWidth,
      mapElement.clientHeight,
      { tooltipWidth, tooltipHeight, verticalGap, edgePadding, verticalTop },
    )
  }, [
    map,
    latitude,
    longitude,
    edgePadding,
    tooltipWidth,
    tooltipHeight,
    verticalGap,
    verticalTop,
  ])

  useEffect(() => {
    const newPlacement = calculatePlacement()
    setPlacement(newPlacement)
  }, [calculatePlacement])

  useEffect(() => {
    if (!map) return

    const handleViewChange = () => {
      const newPlacement = calculatePlacement()
      setPlacement(newPlacement)
    }

    map.addEventListener('mapviewchange', handleViewChange)
    map.addEventListener('mapviewchangeend', handleViewChange)

    return () => {
      map.removeEventListener('mapviewchange', handleViewChange)
      map.removeEventListener('mapviewchangeend', handleViewChange)
    }
  }, [map, calculatePlacement])

  return placement
}

function getDefaultPlacement(): TooltipPlacement {
  return {
    position: 'top',
    horizontalOffset: 0,
    arrowOffset: 0,
    left: 0,
    top: 0,
    transform: 'translate(-50%, -100%)',
    isOutOfViewport: false,
  }
}

/**
 * Hook to measure tooltip height after render
 */
export function useTooltipMeasure() {
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number>(
    DEFAULT_TOOLTIP_HEIGHT,
  )

  useEffect(() => {
    if (tooltipRef.current) {
      const height = tooltipRef.current.offsetHeight
      if (height > 0) {
        setMeasuredHeight(height)
      }
    }
  }, [])

  return { tooltipRef, measuredHeight }
}
